import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CommandContribution, CommandRegistry } from '@patsnap/tahiti-core/common/command';
import { I18nService } from '@telecom/shared/services/i18n';
import IconShorter from '@/presentation/assets/images/icons/icon-shorter.svg';
import { i18nDeferred } from '@/infrastructure/locales/deferred';
import { CurrentTaskService } from '@/services/current-task/current-task-service';

import {
    SpecificationParagraphAiEditType,
    SpecificationParagraphOptimizeType,
    type SpecificationParagraphContentType,
} from '@/domains/specification/enums';
import { StreamStatus } from '@/services/sse-ps-http/enums';
import { SpecificationAiOptimizeParagraphApi } from '@/domains/specification/apis/ai-optimize-specification-paragraph';
import type { PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import type { SpecificationStreamContent } from '@/domains/specification/types/specification';
import { ParagraphTextService } from '@telecom/shared/components/spilt-editor/service/paragraph-text-service';
import { RavenService } from '@telecom/shared/services/raven-service/raven-service';
import * as TrackEvents from '@/constants/track-events';
import { TRACK_EVENT_TYPE } from '@/constants';

/** 缩写 */
@injectable()
export class SimplifyContribution implements CommandContribution {
    constructor(
        @inject(I18nService)
        private _i18nService: I18nService,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(SpecificationAiOptimizeParagraphApi)
        private readonly _aiOptimizeParagraphApi: SpecificationAiOptimizeParagraphApi,
        @inject(ParagraphTextService)
        private readonly _paragraphTextService: ParagraphTextService<SpecificationParagraphContentType>,
        @inject(RavenService)
        private readonly _ravenService: RavenService,
    ) {
    }

    private _stream: PsHttpSSE<SpecificationStreamContent<string>> | null = null;

    generateContent(callback: (status: StreamStatus, content?: string) => void) {
        this._ravenService.track(TrackEvents.TRACK_EVENT_SPECIFICATION_GENERATION_SHORTER, {
            eventType: TRACK_EVENT_TYPE.Specification,
            otherParam: this._currentTaskService.taskId,
            eventDetailStr: {
                paragraphType: this._paragraphTextService.getParagraphTextType(),
            },
        });
        this._stream = this._aiOptimizeParagraphApi.aiOptimizeSpecificationParagraph({
            task_id: this._currentTaskService.taskId,
            content_type: this._paragraphTextService.getParagraphTextType(),
            text: this._paragraphTextService.getParagraphText(),
            optimize_type: SpecificationParagraphOptimizeType.Simplify,
        })
            .onMessage(data => {
                const { status, content } = data.data ?? {};
                callback(status!, content);
            })
            .onFinish(() => {
                callback(StreamStatus.FINISH);
            })
            .onError(() => {
                callback(StreamStatus.FAILED);
            })
            .onClose(() => {
                callback(StreamStatus.FINISH);
            });

        this._stream && this._stream.start();
    }

    public stopGenerate() {
        this._stream?.close();
    }

    async registerCommands(commands: CommandRegistry): Promise<void> {
        await i18nDeferred.promise;

        commands.registerCommand({
            id: SpecificationParagraphAiEditType.Simplify,
            label: this._i18nService.t('aiSpecification.shorter'),
            icon: IconShorter,
        }, {
            execute: () => {
                return {
                    getContentOpt: (callback: (status: StreamStatus, content?: string) => void) => this.generateContent(callback),
                    stopGenerate: () => this.stopGenerate(),
                };
            },
        });
    }
}
